import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { publicApi } from '../utils/api';

const ContactPage = () => {
  const [form, setForm] = useState({
    full_name: '',
    email: '',
    company: '',
    challenge: '',
    note: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [status, setStatus] = useState({ type: '', message: '' });

  const updateField = (field) => (event) => {
    setForm((prev) => ({ ...prev, [field]: event.target.value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSubmitting(true);
    setStatus({ type: '', message: '' });

    try {
      await publicApi.submitLead({
        ...form,
        lead_type: 'consulting',
        source_page: '/contact',
      });
      setStatus({
        type: 'success',
        message: 'Thanks. Your message is in. We will reply with the right next step, usually within 2 business days.',
      });
      setForm({ full_name: '', email: '', company: '', challenge: '', note: '' });
    } catch {
      setStatus({
        type: 'error',
        message: 'We could not send your message right now. You can still reach us directly at hello@wowrolla.ca.',
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen pt-24 page-bg-shell page-bg-grid" data-testid="contact-page">
      <Helmet>
        <title>Contact | Wowrolla</title>
        <meta name="description" content="Contact Wowrolla for support, consulting inquiries, privacy requests, and workflow-specific AI tool guidance." />
      </Helmet>

      <section className="container mx-auto px-6 pb-14 max-w-6xl">
        <h1 className="text-4xl font-black mb-4" style={{ color: 'var(--text-primary)', fontFamily: 'Unbounded, sans-serif' }}>
          Contact Wowrolla
        </h1>
        <p className="text-sm mb-4" style={{ color: 'var(--text-muted)' }}>
          Ottawa, Ontario, Canada
        </p>
        <p className="text-lg max-w-3xl leading-relaxed mb-8" style={{ color: 'var(--text-secondary)' }}>
          Reach out if you need support, want a second opinion on your AI stack, or need help turning tool research into an implementation plan.
          If your question is workflow-specific, include the bottleneck, team size, and current stack so we can respond with something useful.
        </p>

        <div className="grid grid-cols-1 lg:grid-cols-[1.2fr_0.8fr] gap-6">
          <div className="terminal-grid p-6">
            <h2 className="text-2xl font-bold mb-3" style={{ color: 'var(--text-primary)', fontFamily: 'Unbounded, sans-serif' }}>
              Send a message
            </h2>
            <p className="mb-5" style={{ color: 'var(--text-secondary)' }}>
              Best for support questions, consulting interest, partnership inquiries, or clarifying how Wowrolla evaluated a recommendation.
            </p>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <label className="block text-sm" style={{ color: 'var(--text-secondary)' }}>
                  Full name
                  <input value={form.full_name} onChange={updateField('full_name')} className="w-full mt-2 px-3 py-3 bg-transparent border border-white/20 text-white" placeholder="Jordan Smith" required />
                </label>
                <label className="block text-sm" style={{ color: 'var(--text-secondary)' }}>
                  Work email
                  <input type="email" value={form.email} onChange={updateField('email')} className="w-full mt-2 px-3 py-3 bg-transparent border border-white/20 text-white" placeholder="you@company.com" required />
                </label>
              </div>

              <label className="block text-sm" style={{ color: 'var(--text-secondary)' }}>
                Company
                <input value={form.company} onChange={updateField('company')} className="w-full mt-2 px-3 py-3 bg-transparent border border-white/20 text-white" placeholder="Company name" />
              </label>

              <label className="block text-sm" style={{ color: 'var(--text-secondary)' }}>
                Primary challenge
                <input value={form.challenge} onChange={updateField('challenge')} className="w-full mt-2 px-3 py-3 bg-transparent border border-white/20 text-white" placeholder="Too many tools, low confidence, slow implementation, unclear ROI..." required />
              </label>

              <label className="block text-sm" style={{ color: 'var(--text-secondary)' }}>
                Details
                <textarea value={form.note} onChange={updateField('note')} rows={6} className="w-full mt-2 px-3 py-3 bg-transparent border border-white/20 text-white" placeholder="Tell us your workflow, current tools, budget constraints, or what outcome you need to improve." />
              </label>

              {status.message && (
                <p className="text-sm" style={{ color: status.type === 'error' ? '#ff7a7a' : '#9CFB8F' }}>
                  {status.message}
                </p>
              )}

              <div className="flex flex-wrap gap-3 items-center">
                <button type="submit" disabled={submitting} className="btn-primary">
                  {submitting ? 'Sending...' : 'Send message'}
                </button>
                <Link to="/consulting#start" className="px-4 py-3 border" style={{ borderColor: 'var(--border-subtle)', color: 'var(--text-primary)' }}>
                  Prefer a call instead?
                </Link>
              </div>
            </form>
          </div>

          <div className="space-y-5">
            <div className="terminal-grid p-6">
              <h2 className="text-xl font-bold mb-3" style={{ color: 'var(--text-primary)' }}>When to contact us</h2>
              <ul className="space-y-2" style={{ color: 'var(--text-secondary)' }}>
                <li>- You need help narrowing an AI stack for a real workflow, not just comparing feature lists.</li>
                <li>- You want a discovery call before committing to a paid implementation package.</li>
                <li>- You have support, transparency, privacy, or disclosure questions.</li>
                <li>- You want a second opinion on whether your current stack is overbuilt, under-integrated, or low-ROI.</li>
              </ul>
            </div>

            <div className="terminal-grid p-6">
              <h2 className="text-xl font-bold mb-3" style={{ color: 'var(--text-primary)' }}>Direct inboxes</h2>
              <div className="space-y-3" style={{ color: 'var(--text-secondary)' }}>
                <p><span className="text-white">General contact:</span> hello@wowrolla.ca</p>
                <p><span className="text-white">Support issues:</span> support@wowrolla.ca</p>
                <p><span className="text-white">Affiliate and partnerships:</span> partners@wowrolla.ca</p>
              </div>
              <p className="text-sm mt-4" style={{ color: 'var(--text-muted)' }}>
                For privacy requests, use the subject line: Data Request. Typical reply window: 2-5 business days.
              </p>
            </div>

            <div className="terminal-grid p-6">
              <h2 className="text-xl font-bold mb-3" style={{ color: 'var(--text-primary)' }}>Faster paths</h2>
              <div className="space-y-3 text-sm" style={{ color: 'var(--text-secondary)' }}>
                <p><Link to="/find" style={{ color: 'var(--accent-primary)' }}>Use the Finder</Link> if you need immediate tool recommendations.</p>
                <p><Link to="/ai-stacks" style={{ color: 'var(--accent-primary)' }}>Browse AI Stack Prescriptions</Link> if you want workflow-backed examples by industry.</p>
                <p><Link to="/transparency" style={{ color: 'var(--accent-primary)' }}>Read Transparency</Link> if your question is about methodology or affiliate disclosure.</p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;

